import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import { NAV_ITEMS } from '../mock';

// Smooth-scrolls to any in-page #hash target while accounting for the
// fixed header height, and closes the mobile menu after tapping. Falls
// back to default browser jump for non-hash hrefs.
function smoothScrollHandler(setOpen) {
  return (e) => {
    const href = e.currentTarget.getAttribute('href') || '';
    if (href.startsWith('#') && href.length > 1) {
      const el = document.getElementById(href.slice(1));
      if (el) {
        e.preventDefault();
        const headerH = window.innerWidth >= 1024 ? 80 : 64;
        const top = el.getBoundingClientRect().top + window.scrollY - headerH - 8;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
    setOpen(false);
  };
}

/**
 * Navigation — always visible, sticky at top.
 *
 * Earlier version hid the header on scroll-down and revealed it on
 * scroll-up (framer-motion useScroll + useMotionValueEvent). Reverted per
 * user request: users should always be able to navigate without scrolling
 * back up. Only the background blur still animates in based on scroll.
 */
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-black/80 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6 mx-6 xl:mx-10">
          {NAV_ITEMS.map((n) => (
            <a
              key={n.label}
              href={n.href}
              onClick={smoothScrollHandler(setOpen)}
              className="text-[11px] xl:text-[12px] font-medium tracking-[0.14em] uppercase text-zinc-300 hover:text-cyan-300 transition-colors whitespace-nowrap"
            >
              {n.shortLabel || n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="#contact"
            className="btn-teal px-5 py-3 rounded-md text-xs xl:text-sm uppercase tracking-[0.16em] xl:tracking-[0.18em] whitespace-nowrap font-medium"
          >
            Request Quote
          </a>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 border-t border-white/5">
          <div className="px-5 sm:px-6 py-5 flex flex-col gap-4">
            {NAV_ITEMS.map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={smoothScrollHandler(setOpen)}
                className="text-sm tracking-[0.2em] uppercase text-zinc-200"
              >
                {n.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="btn-teal px-5 py-3 rounded-md text-xs uppercase tracking-[0.18em] whitespace-nowrap font-medium inline-flex items-center w-fit"
            >
              Request Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
